/**
 * Where every column on the drill-down tables comes from.
 *
 * These tables put five different kinds of thing side by side: what the student
 * did, what the app claimed, what the scorer computed, what a judge thought of
 * the app, and what deterministic checks fired. Read as one flat row they look
 * equally authoritative — which is how "Student accuracy 10" ends up read as
 * "the analysis was right", when it is the analysis's own arithmetic about an
 * answer it may have misread.
 *
 * So provenance is grouped, tinted and explained rather than left implied. One
 * registry, used by both drill-downs, so a column cannot be labelled one way on
 * one page and another way on the next.
 */

export type SourceGroup = 'attempt' | 'question' | 'tutor' | 'scorer' | 'judge' | 'checks' | 'nav';

interface GroupDef {
    label: string;
    detail: string;
}

export const SOURCE_GROUPS: Record<SourceGroup, GroupDef> = {
    attempt: {
        label: 'The attempt',
        detail: 'What the student actually did, and under which configuration. Read from the '
            + 'session and submission documents — no model involved.',
    },
    question: {
        label: 'The question',
        detail: 'Stored question content, as authored. The answer key the rest of the row is measured against.',
    },
    tutor: {
        label: 'What the app said',
        detail: 'The analysis pipeline\'s own output, written when the student submitted. These are '
            + 'CLAIMS about the student, not judgments of the analysis — a confident wrong claim looks '
            + 'identical to a right one here.',
    },
    scorer: {
        label: 'Scores given',
        detail: 'Computed by the scorer from the tutor\'s assessment, at analyze time. They grade the '
            + 'STUDENT. They inherit any mistake the analysis made, so a high score is not evidence the '
            + 'analysis was correct.',
    },
    judge: {
        label: 'Judged quality',
        detail: 'How good the ANALYSIS was — the only columns that evaluate us rather than the student. '
            + 'Written after the fact by the LLM judge, or by a human overriding it. Empty until this '
            + 'assignment is promoted and judged.',
    },
    checks: {
        label: 'Automatic checks',
        detail: 'Deterministic post-call checks evaluated at analyze time, plus failure status and '
            + 'thumbs-down feedback. No LLM, no cost — the cheapest evidence that something went wrong.',
    },
    nav: { label: '', detail: 'Links to the full pipeline run behind this row.' },
};

interface ColumnDef {
    label: string;
    group: SourceGroup;
    detail: string;
}

export const COLUMNS = {
    student: {
        label: 'Student', group: 'attempt',
        detail: 'The student this row belongs to, from the assignment\'s per-student grant. Opens every '
            + 'solution analysis for them, not just this assignment\'s.',
    },
    attempt: {
        label: 'Attempt', group: 'attempt',
        detail: 'Which attempt produced this analysis, oldest counted as 1. A student who retook the '
            + 'assignment appears once per attempt, because each retake ran the pipeline again over '
            + 'different work — every one of them is separately judgeable. Within one attempt a '
            + 'question can also carry several analyses: re-submitting it, or re-evaluating it, '
            + 'produces a new one and supersedes the last. Those are shown too, marked "superseded" — '
            + 'the attempt no longer points at them, but we produced them and they can be judged.',
    },
    submitted: {
        label: 'Submitted', group: 'attempt',
        detail: 'When the student submitted this attempt. Links to the full pipeline run.',
    },
    config: {
        label: 'Config', group: 'attempt',
        detail: 'The operation config that actually ran this analysis (Submission.pipelineConfig), stamped '
            + 'when the submission was created and never overwritten — so it stays true even after the '
            + 'default config moves, with the model that actually served the analysis beneath it. '
            + '"—" means the submission predates the field.',
    },
    studentAnswer: {
        label: 'Student answer', group: 'attempt',
        detail: 'The answer as submitted — typed text, MCQ selection, or the canvas/photo image — '
            + 'snapshotted onto the submission. This is the raw input the analysis was handed.',
    },
    assignment: {
        label: 'Assignment', group: 'attempt',
        detail: 'The assignment this attempt sat under, resolved from the attempt\'s session. '
            + '"Practice" means it came from somewhere else entirely — self-directed practice, a daily '
            + 'plan or a duel — which is exactly what this page exists to put side by side.',
    },
    question: {
        label: 'Question', group: 'question',
        detail: 'The question text as authored.',
    },
    correctAnswer: {
        label: 'Correct answer', group: 'question',
        detail: 'The question\'s stored answer key.',
    },
    llmRead: {
        label: 'LLM read it as', group: 'tutor',
        detail: 'What the analysis extracted as the student\'s final answer '
            + '(llmAssessment.studentLlmAnswer). Compare it against the student answer and the answer key: '
            + 'a mismatch here is a READING failure, and everything downstream inherits it.',
    },
    tutorVerdict: {
        label: 'App verdict', group: 'tutor',
        detail: 'The analysis\'s own correct/incorrect call (llmAssessment.correctness), written by the '
            + 'analysis pipeline. It is the app\'s claim ABOUT the student — nothing here checks it.',
    },
    studentAccuracy: {
        label: 'Accuracy', group: 'scorer',
        detail: 'The student\'s accuracy score out of 10 (computedScores.correctnessScore). Grades the '
            + 'student, not us. Not to be confused with the accuracy of our ASSESSMENT, under Judged quality.',
    },
    studentApproach: {
        label: 'Approach', group: 'scorer',
        detail: 'The student\'s method score out of 10 (computedScores.approachScore). Blank when the '
            + 'question type carries no approach signal, e.g. a bare MCQ.',
    },
    assessmentQuality: {
        label: 'Assessment quality', group: 'judge',
        detail: 'How good OUR analysis was, on a good / acceptable / bad scale: overall, then "acc" — did '
            + 'it judge WHETHER the answer was right — and "app" — did it judge HOW the student got there. '
            + 'Written by the LLM judge, or a human overriding it. "not judged" means nobody looked; it '
            + 'never means fine.',
    },
    stepVerdicts: {
        label: 'Judge steps', group: 'judge',
        detail: 'Judge the prompt executions behind this analysis one at a time — the reading step '
            + 'separately from the assessment step. Opening it captures this ONE analysis into the '
            + 'assignment\'s eval dataset (not the other rows) so a verdict has something to attach '
            + 'to; the verdicts, failure tags and critique land in eval-verdicts and feed the '
            + 'scorecard. This is the only column that can say WHICH half went wrong.',
    },
    review: {
        label: 'My review', group: 'judge',
        detail: 'Your own thumbs and note on the analysis AS A WHOLE — a triage flag, not a step '
            + 'verdict; use Judge steps to say which half failed. Written into the same feedback array as '
            + 'student and teacher thumbs, and a thumbs-down also files the analysis into the '
            + 'auto-admin-flags eval dataset — so a review here is a labelled example later, not just '
            + 'a note. One review per admin: reviewing again replaces yours, never anyone else\'s.',
    },
    health: {
        label: 'Analysis health', group: 'checks',
        detail: 'Deterministic checks that fired on this run (e.g. objective_llm_answer_mismatch), plus '
            + 'the run\'s own lifecycle — failed, still analyzing. No human judgement and no LLM: these '
            + 'cost nothing and are the first place to look when a score seems too generous. Anyone\'s '
            + 'thumbs lives under My review instead.',
    },
    analysisLink: {
        label: 'Analysis', group: 'nav',
        detail: 'Opens the full pipeline run: every step, its prompt and its raw output.',
    },
} as const satisfies Record<string, ColumnDef>;

export type ColumnKey = keyof typeof COLUMNS;
